import { ICustomTimezone, ITimezone, ITimezoneOption, ITimezoneSelectOptions } from 'my-poppies-b2b-types';
import { useMemo } from 'react';
import spacetime from 'spacetime';
import soft from 'timezone-soft';

import { getObjectEntries } from '@/utils/getObjectEntries';

import type { Spacetime } from 'spacetime';

const allTimezones: ICustomTimezone = {
  'Pacific/Midway': 'Midway Island, Samoa',
  'Pacific/Honolulu': 'Hawaii',
  'America/Juneau': 'Alaska',
  'America/Boise': 'Mountain Time',
  'America/Dawson': 'Dawson, Yukon',
  'America/Chihuahua': 'Chihuahua, La Paz, Mazatlan',
  'America/Phoenix': 'Arizona',
  'America/Chicago': 'Central Time',
  'America/Regina': 'Saskatchewan',
  'America/Mexico_City': 'Guadalajara, Mexico City, Monterrey',
  'America/Belize': 'Central America',
  'America/Detroit': 'Eastern Time',
  'America/Bogota': 'Bogota, Lima, Quito',
  'America/Caracas': 'Caracas, La Paz',
  'America/Santiago': 'Santiago',
  'America/St_Johns': 'Newfoundland and Labrador',
  'America/Sao_Paulo': 'Brasilia',
  'America/Tijuana': 'Tijuana',
  'America/Montevideo': 'Montevideo',
  'America/Argentina/Buenos_Aires': 'Buenos Aires, Georgetown',
  'America/Godthab': 'Greenland',
  'America/Los_Angeles': 'Pacific Time',
  'Atlantic/Azores': 'Azores',
  'Atlantic/Cape_Verde': 'Cape Verde Islands',
  GMT: 'UTC',
  'Europe/London': 'Edinburgh, London',
  'Europe/Dublin': 'Dublin',
  'Europe/Lisbon': 'Lisbon',
  'Africa/Casablanca': 'Casablanca, Monrovia',
  'Atlantic/Canary': 'Canary Islands',
  'Europe/Belgrade': 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
  'Europe/Sarajevo': 'Sarajevo, Skopje, Warsaw, Zagreb',
  'Europe/Brussels': 'Brussels, Copenhagen, Madrid, Paris',
  'Europe/Amsterdam': 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
  'Africa/Algiers': 'West Central Africa',
  'Europe/Bucharest': 'Bucharest',
  'Africa/Cairo': 'Cairo',
  'Europe/Helsinki': 'Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
  'Europe/Athens': 'Athens',
  'Asia/Jerusalem': 'Jerusalem',
  'Africa/Harare': 'Harare, Pretoria',
  'Europe/Moscow': 'Istanbul, Minsk, Moscow, St. Petersburg, Volgograd',
  'Asia/Kuwait': 'Kuwait, Riyadh',
  'Africa/Nairobi': 'Nairobi',
  'Asia/Baghdad': 'Baghdad',
  'Asia/Tehran': 'Tehran',
  'Asia/Dubai': 'Abu Dhabi, Muscat',
  'Asia/Baku': 'Baku, Tbilisi, Yerevan',
  'Asia/Kabul': 'Kabul',
  'Asia/Yekaterinburg': 'Ekaterinburg',
  'Asia/Karachi': 'Islamabad, Karachi, Tashkent',
  'Asia/Kolkata': 'Chennai, Kolkata, Mumbai, New Delhi',
  'Asia/Kathmandu': 'Kathmandu',
  'Asia/Dhaka': 'Astana, Dhaka',
  'Asia/Colombo': 'Sri Jayawardenepura',
  'Asia/Almaty': 'Almaty, Novosibirsk',
  'Asia/Rangoon': 'Yangon Rangoon',
  'Asia/Bangkok': 'Bangkok, Hanoi, Jakarta',
  'Asia/Krasnoyarsk': 'Krasnoyarsk',
  'Asia/Shanghai': 'Beijing, Chongqing, Hong Kong SAR, Urumqi',
  'Asia/Kuala_Lumpur': 'Kuala Lumpur, Singapore',
  'Asia/Taipei': 'Taipei',
  'Australia/Perth': 'Perth',
  'Asia/Irkutsk': 'Irkutsk, Ulaanbaatar',
  'Asia/Seoul': 'Seoul',
  'Asia/Tokyo': 'Osaka, Sapporo, Tokyo',
  'Asia/Yakutsk': 'Yakutsk',
  'Australia/Darwin': 'Darwin',
  'Australia/Adelaide': 'Adelaide',
  'Australia/Sydney': 'Canberra, Melbourne, Sydney',
  'Australia/Brisbane': 'Brisbane',
  'Australia/Hobart': 'Hobart',
  'Asia/Vladivostok': 'Vladivostok',
  'Pacific/Guam': 'Guam, Port Moresby',
  'Asia/Magadan': 'Magadan, Solomon Islands, New Caledonia',
  'Asia/Kamchatka': 'Kamchatka, Marshall Islands',
  'Pacific/Fiji': 'Fiji Islands',
  'Pacific/Auckland': 'Auckland, Wellington',
  'Pacific/Tongatapu': "Nuku'alofa",
};

export function useTimezoneSelect({
  timezones = allTimezones,
  labelStyle = 'original',
  displayValue = 'GMT',
  currentDatetime,
}: ITimezoneSelectOptions): {
  parseTimezone: (zone: ITimezone) => ITimezoneOption;
  options: ITimezoneOption[];
} {
  const options: ITimezoneOption[] = useMemo(() => {
    const unsorted: ITimezoneOption[] = getObjectEntries(timezones)
      .map((zone) => {
        try {
          const now = (currentDatetime ? spacetime(currentDatetime) : spacetime.now()).goto(zone[0]);
          const isDstString = now.isDST() ? 'daylight' : 'standard';
          const tz = now.timezone();
          const tzStrings = soft(zone[0]);

          const abbr = tzStrings?.[0]?.[isDstString]?.abbr;
          const altName = tzStrings?.[0]?.[isDstString]?.name;

          const min = tz.current.offset * 60;
          const hr = `${(min / 60) ^ 0}:${min % 60 === 0 ? '00' : Math.abs(min % 60)}`;
          const prefix = `(${displayValue}${hr.includes('-') ? hr : `+${hr}`}) ${zone[1]}`;

          let label = '';

          switch (labelStyle) {
            case 'original':
              label = prefix;
              break;
            case 'altName':
              label = `${prefix} ${altName ? `(${altName})` : ''}`;
              break;
            case 'abbrev':
              label = `${prefix} ${abbr ? `(${abbr})` : ''}`;
              break;
            case 'offsetHidden':
              label = `${prefix.replace(/^\(.*?\)\s*/, '')}`;
              break;
            default:
              label = `${prefix}`;
          }

          return {
            value: tz.name,
            label: label,
            offset: tz.current.offset,
            abbrev: abbr,
            altName: altName,
          };
        } catch {
          return undefined;
        }
      })
      .filter(Boolean) as ITimezoneOption[];
    return unsorted.sort((a: ITimezoneOption, b: ITimezoneOption) => a.offset - b.offset);
  }, [timezones, currentDatetime, displayValue, labelStyle]);

  const findFuzzyTz = (zone: string): ITimezoneOption => {
    let currentTime: Spacetime;
    try {
      currentTime = (currentDatetime ? spacetime(currentDatetime) : spacetime.now()).goto(zone);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      currentTime = (currentDatetime ? spacetime(currentDatetime) : spacetime.now()).goto('GMT');
    }

    return options
      .filter((tz: ITimezoneOption) => tz.offset === currentTime.timezone().current.offset)
      .map((tz: ITimezoneOption) => {
        let score = 0;
        if (
          currentTime.timezones[tz.value.toLowerCase()] &&
          !!currentTime.timezones[tz.value.toLowerCase()].dst === currentTime.timezone().hasDst
        ) {
          if (tz.value.toLowerCase().indexOf(currentTime.tz.substring(currentTime.tz.indexOf('/') + 1)) !== -1) {
            score += 8;
          }
          if (tz.label.toLowerCase().indexOf(currentTime.tz.substring(currentTime.tz.indexOf('/') + 1)) !== -1) {
            score += 4;
          }
          if (tz.value.toLowerCase().indexOf(currentTime.tz.substring(0, currentTime.tz.indexOf('/'))) !== -1) {
            score += 2;
          }
          score += 1;
        } else if (tz.value === 'GMT') {
          score += 1;
        }
        return { tz, score };
      })
      .sort((a, b) => b.score - a.score)?.[0]?.tz;
  };

  function isObject(item: unknown) {
    return typeof item === 'object' && !Array.isArray(item) && item !== null;
  }

  const parseTimezone = (zone: ITimezone) => {
    if (typeof zone === 'string') {
      return options.find((tz) => tz.value === zone)! || (zone.indexOf('/') !== -1 && findFuzzyTz(zone))!;
    } else if (isObject(zone) && !zone.label) {
      return options.find((tz) => tz.value === zone.value)!;
    } else {
      return zone;
    }
  };

  return { options, parseTimezone };
}
